import React, { useState, useEffect, useMemo, useRef } from "react";
import Modal from "react-bootstrap/Modal";

const FieldLabel = ({ children }) => (
  <div
    style={{
      color: "#6b7280",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const focusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = "#ff5100";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.boxShadow = "none";
  },
};

const CreateTagAssignment = ({
  domain,
  show,
  isBrandAdmin,
  clientId,
  handleClose,
  handleFormSubmit,
  orderAssignmentId,
  modalData,
}) => {
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [skuSearch, setSkuSearch] = useState("");
  const [skuOpen, setSkuOpen] = useState(false);
  const skuDropdownRef = useRef(null);
  const [minSerial, setMinSerial] = useState("");
  const [maxSerial, setMaxSerial] = useState("");
  const [metadataFields, setMetadataFields] = useState([]);
  const [userEmail] = useState("adrenreuel@gmail.com");
  const [serialRangeUpperBound, setSerialRangeUpperBound] = useState(null);
  const [orderMinBound, setOrderMinBound] = useState(null);
  const [maxSerialError, setMaxSerialError] = useState("");
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [isMinSerialDisabled, setIsMinSerialDisabled] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [isMaxManual, setIsMaxManual] = useState(false);

  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null;
    return items.find((i) => i._id === selectedItemId) || null;
  }, [items, selectedItemId]);

  const filteredItems = useMemo(() => {
    const q = skuSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => {
      const code = (i.code || "").toLowerCase();
      const name = (i.metadata?.name || "").toLowerCase();
      return code.includes(q) || name.includes(q);
    });
  }, [items, skuSearch]);

  useEffect(() => {
    if (!skuOpen) return;
    const handleOutside = (e) => {
      if (skuDropdownRef.current && !skuDropdownRef.current.contains(e.target)) {
        setSkuOpen(false);
      }
    };
    const handleEsc = (e) => { if (e.key === "Escape") setSkuOpen(false); };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [skuOpen]);

  useEffect(() => {
    if (!show) {
      // Reset bounds on close so validation deps change on the next open
      setSerialRangeUpperBound(null);
      setOrderMinBound(null);
      return;
    }

    if (isBrandAdmin && !clientId) return;

    const fetchItems = async () => {
      try {
        const url =
          isBrandAdmin && clientId
            ? `https://api.iriscrypt.com/item/by-client/${encodeURIComponent(clientId)}`
            : "https://api.iriscrypt.com/item";

        const response = await fetch(url, {
          method: "GET",
          headers: { "x-api-key": "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43" },
        });
        const result = await response.json();
        setItems(result || []);
      } catch (error) {
        console.error("Failed to fetch items:", error);
      }
    };

    fetchItems();
    resetForm();

    const serialRange = modalData?.serialRange;
    if (serialRange) {
      const [minStr, maxStr] = serialRange.split(" - ");
      const orderMin = parseInt((minStr || "").trim(), 10);
      const orderMax = parseInt((maxStr || "").trim(), 10);

      if (!Number.isFinite(orderMin) || !Number.isFinite(orderMax)) {
        console.error("Invalid order serial range:", serialRange);
        setMaxSerialError("Invalid order serial range.");
        setIsSubmitDisabled(true);
        return;
      }

      setOrderMinBound(orderMin);
      setSerialRangeUpperBound(orderMax);

      if (Array.isArray(modalData?.tagAssignments) && modalData.tagAssignments.length > 0) {
        const mostRecent = modalData.tagAssignments
          .slice()
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        setMinSerial(Number(mostRecent?.maxSerial) + 1);
      } else {
        setMinSerial(orderMin);
      }

      setIsMinSerialDisabled(true);
      setMaxSerial(orderMax);
    }
  }, [show, modalData, isBrandAdmin, clientId]);

  useEffect(() => {
    if (!show || isMaxManual) return;
    const parsedMin = parseInt(minSerial, 10);
    const qty = parseInt(quantity, 10);
    if (!Number.isFinite(parsedMin) || !Number.isFinite(qty) || qty <= 0) return;
    const computedMax = parsedMin + qty - 1;
    setMaxSerial(String(computedMax));
    validateMaxSerial(computedMax, parsedMin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity, minSerial, serialRangeUpperBound, orderMinBound, show, isMaxManual]);

  // Validate on initialization (when bounds/serials are set from modalData but quantity is still empty)
  useEffect(() => {
    if (!show || serialRangeUpperBound === null) return;
    const parsedMax = parseInt(maxSerial, 10);
    const parsedMin = parseInt(minSerial, 10);
    if (!Number.isFinite(parsedMax) || !Number.isFinite(parsedMin)) return;
    validateMaxSerial(parsedMax, parsedMin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minSerial, maxSerial, serialRangeUpperBound, show]);

  const validateMaxSerial = (parsedMax, parsedMin) => {
    if (!Number.isFinite(parsedMax)) {
      setMaxSerialError("Please enter a valid number for Max Serial.");
      setIsSubmitDisabled(true);
      return false;
    }
    if (serialRangeUpperBound != null && parsedMax > serialRangeUpperBound) {
      setMaxSerialError(`Max serial cannot exceed ${serialRangeUpperBound}.`);
      setIsSubmitDisabled(true);
      return false;
    }
    if (Number.isFinite(parsedMin) && parsedMax < parsedMin) {
      setMaxSerialError("Max serial cannot be less than Min serial.");
      setIsSubmitDisabled(true);
      return false;
    }
    setMaxSerialError("");
    setIsSubmitDisabled(false);
    return true;
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    setQuantity(value);
    if (value === "") return;
    const qty = parseInt(value, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      setIsSubmitDisabled(true);
      setMaxSerialError("Please enter a valid quantity (>= 1).");
      return;
    }
    setIsMaxManual(false);
    const parsedMin = parseInt(minSerial, 10);
    if (Number.isFinite(parsedMin)) {
      validateMaxSerial(parsedMin + qty - 1, parsedMin);
    }
  };

  const handleMaxSerialChange = (e) => {
    const value = e.target.value;
    setMaxSerial(value);
    setIsMaxManual(true);
    const parsedMax = parseInt(value, 10);
    const parsedMin = parseInt(minSerial, 10);
    if (Number.isFinite(parsedMax) && Number.isFinite(parsedMin) && parsedMax >= parsedMin) {
      setQuantity(String(parsedMax - parsedMin + 1));
    }
    validateMaxSerial(parsedMax, parsedMin);
  };

  const handleSubmit = async () => {
    const item = items.find((i) => i._id === selectedItemId);
    const parsedMin = parseInt(minSerial, 10);
    const parsedMax = parseInt(maxSerial, 10);

    if (!item || !Number.isFinite(parsedMin) || !Number.isFinite(parsedMax)) {
      alert("Please fill all required fields.");
      return;
    }
    if (orderMinBound != null && parsedMin < orderMinBound) {
      alert(`Min serial must be at least ${orderMinBound}.`);
      return;
    }
    if (serialRangeUpperBound != null && parsedMax > serialRangeUpperBound) {
      alert(`Max serial cannot exceed ${serialRangeUpperBound}.`);
      return;
    }
    if (parsedMax < parsedMin) {
      alert("Max serial cannot be less than Min serial.");
      return;
    }

    const metadata = {};
    for (const field of metadataFields) {
      if (field.key && field.value) metadata[field.key] = field.value;
    }

    const payload = {
      orderAssignmentId,
      itemId: item._id,
      itemCode: item.code,
      minSerial: parsedMin,
      maxSerial: parsedMax,
      metadata,
      user: userEmail,
      client: clientId,
    };

    try {
      const response = await fetch("https://api.iriscrypt.com/tagAssignment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      let data;
      try { data = text ? JSON.parse(text) : {}; } catch { data = { message: text }; }

      if (!response.ok) {
        const msg = data?.message || data?.error || data?.detail?.[0]?.msg || `Request failed with status ${response.status}`;
        console.error("Create tag assignment failed:", { status: response.status, payload, server: data });
        alert(msg);
        return;
      }
      if (data?.success === false) {
        alert(data?.message || "Failed to create assignment.");
        return;
      }

      handleClose();
      handleFormSubmit();
    } catch (error) {
      console.error("Assignment submit error:", error);
      alert("An unexpected error occurred while creating the assignment.");
    }
  };

  const resetForm = () => {
    setSelectedItemId("");
    setSkuSearch("");
    setSkuOpen(false);
    setMinSerial("");
    setMaxSerial("");
    setMetadataFields([]);
    setMaxSerialError("");
    setIsSubmitDisabled(false);
    setIsMinSerialDisabled(false);
    setQuantity("");
    setIsMaxManual(false);
  };

  const addMetadataField = () => setMetadataFields((prev) => [...prev, { key: "", value: "" }]);
  const removeMetadataField = (index) => {
    const updated = [...metadataFields];
    updated.splice(index, 1);
    setMetadataFields(updated);
  };
  const updateMetadataField = (index, keyOrValue, newValue) => {
    const updated = [...metadataFields];
    updated[index][keyOrValue] = newValue;
    setMetadataFields(updated);
  };
  const moveFieldUp = (index) => {
    if (index === 0) return;
    const updated = [...metadataFields];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setMetadataFields(updated);
  };
  const moveFieldDown = (index) => {
    if (index === metadataFields.length - 1) return;
    const updated = [...metadataFields];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setMetadataFields(updated);
  };

  const renderValue = (v) => {
    if (v == null) return "";
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
    try { return JSON.stringify(v); } catch { return String(v); }
  };

  return (
    <>
      <style>{`
        .create-tag-assignment-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .create-tag-assignment-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .create-tag-assignment-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .create-tag-assignment-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        dialogClassName="create-tag-assignment-modal"
        centered
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <i className="fas fa-tag" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Create Assignment</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                Assign tags to a product SKU
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* Product SKU searchable select */}
          <div ref={skuDropdownRef} style={{ position: "relative" }}>
            <FieldLabel>Product SKU *</FieldLabel>
            <div
              onClick={() => setSkuOpen((o) => !o)}
              style={{
                ...inputStyle,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                userSelect: "none",
                borderColor: skuOpen ? "#ff5100" : "#e5e7eb",
                boxShadow: skuOpen ? "0 0 0 3px rgba(255,81,0,0.1)" : "none",
              }}
            >
              <span style={{ color: selectedItem ? "#111827" : "#9ca3af" }}>
                {selectedItem
                  ? `${selectedItem.code}${selectedItem.metadata?.name ? ` — ${selectedItem.metadata.name}` : ""}`
                  : "Select a product SKU…"}
              </span>
              <i
                className={`fas fa-chevron-${skuOpen ? "up" : "down"}`}
                style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0, marginLeft: 8 }}
              />
            </div>

            {skuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: 12,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
                  zIndex: 100,
                  overflow: "hidden",
                }}
              >
                {/* Search input */}
                <div style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "7px 10px" }}>
                    <i className="fas fa-search" style={{ color: "#9ca3af", fontSize: 12, flexShrink: 0 }} />
                    <input
                      autoFocus
                      type="text"
                      value={skuSearch}
                      onChange={(e) => setSkuSearch(e.target.value)}
                      placeholder="Search by code or name…"
                      style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, color: "#111827", width: "100%" }}
                    />
                    {skuSearch && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setSkuSearch(""); }}
                        style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 0, fontSize: 12, display: "flex", alignItems: "center" }}
                      >
                        <i className="fas fa-times" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Options list */}
                <div style={{ maxHeight: 220, overflowY: "auto" }}>
                  {filteredItems.length === 0 ? (
                    <div style={{ padding: "14px 14px", fontSize: 13, color: "#9ca3af", textAlign: "center" }}>
                      No SKUs match "{skuSearch}"
                    </div>
                  ) : (
                    filteredItems.map((item) => {
                      const isSelected = item._id === selectedItemId;
                      return (
                        <div
                          key={item._id}
                          onClick={() => {
                            setSelectedItemId(item._id);
                            setSkuOpen(false);
                            setSkuSearch("");
                          }}
                          style={{
                            padding: "10px 14px",
                            cursor: "pointer",
                            background: isSelected ? "rgba(255,81,0,0.06)" : "transparent",
                            borderLeft: isSelected ? "3px solid #ff5100" : "3px solid transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f9fafb"; }}
                          onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                        >
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "#ff5100" : "#111827" }}>
                              {item.code}
                            </div>
                            {item.metadata?.name && (
                              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>{item.metadata.name}</div>
                            )}
                          </div>
                          {isSelected && (
                            <i className="fas fa-check" style={{ fontSize: 12, color: "#ff5100", flexShrink: 0 }} />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* SKU Preview */}
          {selectedItem && (
            <div
              style={{
                marginTop: 12,
                border: "1.5px solid #e5e7eb",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  background: "#f9fafb",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Selected SKU
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", marginTop: 2 }}>
                    {selectedItem.code}
                  </div>
                </div>
                <div
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "4px 10px", borderRadius: 999,
                    background: "rgba(255,81,0,0.08)",
                    border: "1px solid rgba(255,81,0,0.2)",
                    fontSize: 11, fontWeight: 700, color: "#ff5100",
                  }}
                >
                  <i className="fas fa-eye" style={{ fontSize: 10 }} />
                  Preview
                </div>
              </div>

              <div style={{ padding: "10px 14px" }}>
                {selectedItem.metadata && Object.keys(selectedItem.metadata).length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {Object.entries(selectedItem.metadata).map(([k, v]) => (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <span style={{ color: "#6b7280", fontWeight: 600, textTransform: "uppercase", fontSize: 11, letterSpacing: "0.04em" }}>
                          {k}
                        </span>
                        <span style={{ color: "#111827", fontWeight: 500 }}>
                          {renderValue(v) || <span style={{ color: "#d1d5db" }}>—</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: "#9ca3af" }}>No metadata for this SKU.</div>
                )}

                {(selectedItem.timestamp || selectedItem.updated_at) && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f3f4f6", display: "flex", gap: 16, fontSize: 11, color: "#9ca3af" }}>
                    {selectedItem.timestamp && (
                      <span>Added {new Date(selectedItem.timestamp).toLocaleDateString()}</span>
                    )}
                    {selectedItem.updated_at && (
                      <span>Updated {new Date(selectedItem.updated_at).toLocaleDateString()}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Serial range */}
          <div style={{ marginTop: 16 }}>
            <FieldLabel>Serial Range *</FieldLabel>
            <div style={{ display: "flex", border: "1.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ flex: 1, padding: "10px 14px", background: "#f9fafb" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.05em", marginBottom: 4 }}>FROM</div>
                <input
                  type="number"
                  value={minSerial}
                  onChange={(e) => setMinSerial(e.target.value)}
                  disabled={isMinSerialDisabled}
                  placeholder="Min serial"
                  style={{
                    width: "100%", border: "none", outline: "none",
                    fontSize: 14, fontWeight: 600,
                    color: "#6b7280", background: "transparent", padding: 0,
                  }}
                />
              </div>
              <div style={{ width: 1, background: "#e5e7eb", alignSelf: "stretch" }} />
              <div style={{ flex: 1, padding: "10px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.05em" }}>TO</span>
                  {isMaxManual && (
                    <span style={{ fontSize: 10, color: "#f59e0b", fontWeight: 600 }}>manual</span>
                  )}
                </div>
                <input
                  type="number"
                  value={maxSerial}
                  onChange={handleMaxSerialChange}
                  placeholder="Max serial"
                  style={{
                    width: "100%", border: "none", outline: "none",
                    fontSize: 14, fontWeight: 600,
                    color: "#111827", background: "transparent", padding: 0,
                  }}
                />
              </div>
            </div>
            {maxSerialError && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#dc2626", display: "flex", alignItems: "center", gap: 5 }}>
                <i className="fas fa-exclamation-circle" style={{ fontSize: 11 }} />
                {maxSerialError}
              </div>
            )}
          </div>

          {/* Quantity */}
          <div style={{ marginTop: 16 }}>
            <FieldLabel>Quantity</FieldLabel>
            <input
              type="number"
              min={1}
              value={quantity}
              onChange={handleQuantityChange}
              placeholder="Enter quantity to auto-fill max serial"
              style={inputStyle}
              {...focusHandlers}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: "#9ca3af", display: "flex", alignItems: "center", gap: 5 }}>
              <i className="fas fa-info-circle" style={{ fontSize: 11 }} />
              {isMaxManual
                ? "Max serial is set manually — change quantity to re-calculate."
                : "Auto-calculates Max Serial. You can still edit Max directly."}
            </div>
          </div>

          {/* Metadata section note */}
          <div
            style={{
              marginTop: 20,
              background: "#f0f9ff",
              border: "1.5px solid #bae6fd",
              borderRadius: 10,
              padding: "10px 14px",
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
            }}
          >
            <i className="fas fa-circle-info" style={{ color: "#0284c7", fontSize: 13, marginTop: 1, flexShrink: 0 }} />
            <div style={{ fontSize: 12.5, color: "#0369a1", lineHeight: 1.6 }}>
              <span style={{ fontWeight: 700 }}>Variable Metadata</span> is for batch-specific details that change per production run, e.g. manufacturing date, expiry date, dispatch number, or batch code.
              This is separate from the Product SKU metadata, which captures fixed product specs like size or volume.
            </div>
          </div>

          {/* Metadata fields */}
          {metadataFields.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <FieldLabel>Variable Metadata</FieldLabel>
              {metadataFields.map((field, index) => (
                <div
                  key={index}
                  style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}
                >
                  <input
                    type="text"
                    value={field.key}
                    onChange={(e) => updateMetadataField(index, "key", e.target.value)}
                    placeholder="Key"
                    style={{ ...inputStyle, flex: 1 }}
                    {...focusHandlers}
                  />
                  <input
                    type="text"
                    value={field.value}
                    onChange={(e) => updateMetadataField(index, "value", e.target.value)}
                    placeholder="Value"
                    style={{ ...inputStyle, flex: 1 }}
                    {...focusHandlers}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 3, flexShrink: 0 }}>
                    <button
                      onClick={() => moveFieldUp(index)}
                      disabled={index === 0}
                      style={{
                        width: 28, height: 28, borderRadius: 6,
                        border: "1.5px solid #e5e7eb", background: "#fff",
                        color: index === 0 ? "#d1d5db" : "#6b7280",
                        cursor: index === 0 ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11,
                      }}
                    >
                      <i className="fas fa-chevron-up" />
                    </button>
                    <button
                      onClick={() => moveFieldDown(index)}
                      disabled={index === metadataFields.length - 1}
                      style={{
                        width: 28, height: 28, borderRadius: 6,
                        border: "1.5px solid #e5e7eb", background: "#fff",
                        color: index === metadataFields.length - 1 ? "#d1d5db" : "#6b7280",
                        cursor: index === metadataFields.length - 1 ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11,
                      }}
                    >
                      <i className="fas fa-chevron-down" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeMetadataField(index)}
                    style={{
                      width: 32, height: 32, borderRadius: 8,
                      border: "1.5px solid #fecaca", background: "#fff5f5",
                      color: "#dc2626", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, flexShrink: 0,
                    }}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={addMetadataField}
            style={{
              marginTop: metadataFields.length > 0 ? 4 : 12,
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "8px 14px", borderRadius: 9,
              border: "1.5px dashed #d1d5db", background: "transparent",
              color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer",
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#ff5100";
              e.currentTarget.style.color = "#ff5100";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#d1d5db";
              e.currentTarget.style.color = "#6b7280";
            }}
          >
            <i className="fas fa-plus" style={{ fontSize: 11 }} />
            Add variable batch-wise metadata
          </button>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            style={{
              width: "100%",
              padding: "11px 0",
              borderRadius: 10,
              border: "none",
              background: isSubmitDisabled
                ? "#e5e7eb"
                : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
              color: isSubmitDisabled ? "#9ca3af" : "#fff",
              fontWeight: 700,
              fontSize: 15,
              cursor: isSubmitDisabled ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 9,
              transition: "background 0.15s",
            }}
          >
            Create Assignment
            <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateTagAssignment;
